import { Box, Button, Typography } from '@mui/material';
import { StyledAccountApproval, StyledPlayerDetails } from './style';
import { FC, useMemo, useState } from 'react';
import { AccountApprovalModalDesign } from 'utils/types';

interface IAccountApprovalModalProps {
  onApproval: () => void;
  onDisapproval: () => void;
  isDisabled?: boolean;
  design?: AccountApprovalModalDesign;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  translations?: Record<string, string>;
  playerName?: string;
  playerImage?: string;
}

const DEFAULT_TRANSLATIONS = {
  title: 'Is This Your Account?',
  approve: 'Approve',
  disapprove: 'Not me',
  byClicking: 'By clicking approve you have read and agree to',
  and: 'And',
  privacyPolicy: 'Privacy Policy',
  termsAndConditions: 'Terms and Conditions',
};

export const AccountApproval: FC<IAccountApprovalModalProps> = ({
  onApproval,
  onDisapproval,
  isDisabled = false,
  design,
  privacyPolicyUrl,
  termsAndConditionsUrl,
  translations,
  playerName,
  playerImage = 'https://media.appcharge.com/defaults/profile.png',
}) => {
  const [picLoaded, setPicLoaded] = useState(false);

  const approvalModalTranslations = useMemo(
    () => translations ?? DEFAULT_TRANSLATIONS,
    [translations],
  );

  return (
    <StyledAccountApproval
      className="account-approval"
      gap={'12px'}
      backgroundImage={design?.backgroundImage}
      backgroundColor={
        design?.backgroundColor || {
          colorOne: '#80809d',
        }
      }
      borderWidth={design?.borderWidth}
      borderColor={design?.borderColor}
      buttonBackgroundColor={
        design?.buttonBackgroundColor || {
          colorOne: '#46B2B0',
        }
      }
      buttonTextColor={design?.buttonTextColor}
      textColor={design?.textColor}
      as={Box}
      isAdditionalTextShown={Boolean(design?.additionalText)}
    >
      <Typography
        component="h2"
        fontSize={18}
        fontWeight={600}
        className="account-approval-text"
      >
        {approvalModalTranslations.title}
      </Typography>
      <StyledPlayerDetails gap={'4px'}>
        <div className="player-image">
          <img
            alt="profile"
            className="profileContainer__img"
            src={playerImage}
            style={{ display: picLoaded ? 'unset' : 'none' }}
            onLoad={() => setPicLoaded(true)}
          />
          <img
            alt="profile"
            className="profileContainer__img"
            src="https://media.appcharge.com/defaults/old-profile.png"
            style={{ display: picLoaded ? 'none' : 'unset' }}
          />
        </div>
        {playerName && (
          <Typography
            component="h2"
            fontSize={14}
            fontWeight={500}
            className="account-approval-text"
          >
            {playerName}
          </Typography>
        )}
      </StyledPlayerDetails>
      <Button
        className="approve-button"
        disabled={isDisabled}
        onClick={onApproval}
      >
        {approvalModalTranslations.approve}
      </Button>
      <Button
        className="disapprove-button"
        variant="text"
        disabled={isDisabled}
        onClick={onDisapproval}
      >
        {approvalModalTranslations.disapprove}
      </Button>
      <Box>
        {design?.additionalText && (
          <Typography className="additional-text account-approval-text">
            {design?.additionalText}
          </Typography>
        )}
        <Typography
          className="footer account-approval-text"
          sx={{
            fontSize: '10px',
            textAlign: 'center',
          }}
        >
          {approvalModalTranslations.byClicking}{' '}
          <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
            {approvalModalTranslations.privacyPolicy}
          </a>{' '}
          {approvalModalTranslations.and}{' '}
          <a href={termsAndConditionsUrl} target="_blank" rel="noreferrer">
            {approvalModalTranslations.termsAndConditions}
          </a>
        </Typography>
      </Box>
    </StyledAccountApproval>
  );
};
